import { useQuery } from '@tanstack/react-query';
import type { User as OidcUser } from 'oidc-client-ts';
import React, { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth as useOidcAuth, type ErrorContext } from 'react-oidc-context';
import { Roles } from '@/api/model';
import { apiInstance, setApiAuth } from '@/lib/apiInstance';

interface User {
  id: string;
  name: string;
  email: string;
  age: number;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Response from GET /api/auth/me/access – voucher portal access and contract end */
interface CustomerAccessResponse {
  has_voucher_portal_access: boolean;
  running_contract_until?: string | null;
}

const ACCESS_CACHE_KEY = 'auth_me_access';

function getCachedAccess(userId: string | undefined): CustomerAccessResponse | null {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const raw = sessionStorage.getItem(ACCESS_CACHE_KEY);
    if (!raw) return null;
    const { userId: cachedUserId, data } = JSON.parse(raw) as {
      userId: string;
      data: CustomerAccessResponse;
    };
    return cachedUserId === userId ? data : null;
  } catch {
    return null;
  }
}

function setCachedAccess(userId: string, data: CustomerAccessResponse): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(ACCESS_CACHE_KEY, JSON.stringify({ userId, data }));
  } catch {
    // ignore
  }
}

function clearCachedAccess(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(ACCESS_CACHE_KEY);
  } catch {
    // ignore
  }
}

// Replacable interface as abstraction layer for user authentication and context
interface AuthContextType {
  user: User | null | undefined;
  /** Backend user id (from /api/auth/me); use to compare with list/detail ids (e.g. prevent editing self). */
  currentUserId: string | null;
  /** From GET /api/auth/me/access (SWD). Fetched on login. */
  hasVoucherPortalAccess: boolean;
  /** When set and in the past, user cannot activate vouchers. */
  runningContractUntil: string | null;
  token: string | null;
  isLoading: boolean;
  /** True while GET /api/auth/me/access is in progress (after OIDC auth). */
  isAccessLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: () => void;
  logout: () => Promise<void>;
  error: ErrorContext | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/** Response from GET /api/auth/me – backend determines admin from ADMIN_EMAILS */
interface CurrentUserResponse {
  username: string;
  id: string;
  email: string | null;
  roles: string[];
}

function fetchBackendCurrentUser(): Promise<CurrentUserResponse> {
  return apiInstance<CurrentUserResponse>({
    url: '/api/auth/me',
    method: 'GET',
  });
}

function fetchBackendCustomerAccess(): Promise<CustomerAccessResponse> {
  // Backend proxies SWD call to avoid CORS issues
  return apiInstance<CustomerAccessResponse>({
    url: '/api/auth/me/access',
    method: 'GET',
  });
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const oidcAuth = useOidcAuth();
  const userId = oidcAuth.user?.profile?.sub;
  const cachedAccess = getCachedAccess(userId);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const {
    data: accessData,
    isLoading: isAccessLoading,
    isSuccess: isAccessSuccess,
  } = useQuery({
    queryKey: ['auth', 'me/access', userId],
    queryFn: fetchBackendCustomerAccess,
    enabled: oidcAuth.isAuthenticated === true && !!userId && cachedAccess === null && !isLoggingOut,
    initialData: cachedAccess ?? undefined,
  });

  // Reset logging-out flag when auth state is cleared (e.g. after redirect)
  useEffect(() => {
    if (!oidcAuth.isAuthenticated) {
      setIsLoggingOut(false);
    }
  }, [oidcAuth.isAuthenticated]);

  // Persist access data to sessionStorage after fetch (only on login, not on refresh)
  useEffect(() => {
    if (isAccessSuccess && accessData && userId) {
      setCachedAccess(userId, accessData);
    }
  }, [isAccessSuccess, accessData, userId]);

  const { data: backendUser } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchBackendCurrentUser,
    enabled:
      oidcAuth.isAuthenticated === true && isAccessSuccess === true && accessData?.has_voucher_portal_access === true,
  });

  const isAdminFromBackend = backendUser?.roles?.includes(Roles.Admin) ?? false;

  // Convert OIDC user to our custom User type
  const convertOidcUserToUser = (oidcUser: OidcUser | null | undefined): User | null | undefined => {
    if (!oidcUser) return oidcUser;

    // Extract user data from OIDC user profile or token claims.
    // isAdmin comes from backend (ADMIN_EMAILS) when available.
    return {
      id: oidcUser.profile?.sub || '0',
      name:
        [oidcUser.profile?.given_name, oidcUser.profile?.family_name].filter(Boolean).join(' ') ||
        oidcUser.profile?.name ||
        '',
      email: oidcUser.profile?.email || '',
      age: parseInt((oidcUser.profile?.age as string) || '0'),
      isAdmin: isAdminFromBackend,
      createdAt: oidcUser.profile?.iat ? new Date(oidcUser.profile.iat * 1000).toISOString() : new Date().toISOString(),
      updatedAt: oidcUser.profile?.updated_at
        ? new Date(Number(oidcUser.profile.updated_at) * 1000).toISOString()
        : new Date().toISOString(),
    };
  };

  const login = useCallback(() => {
    oidcAuth.signinRedirect();
  }, [oidcAuth]);

  // Wire API layer to use the same token and login as this context (react-oidc-context),
  // so API calls (e.g. access-control, vouchers) work after login.
  useEffect(() => {
    setApiAuth(() => oidcAuth.user?.access_token ?? null, login);
  }, [oidcAuth.user?.access_token, login]);

  const logout = async () => {
    setIsLoggingOut(true);
    clearCachedAccess();
    await oidcAuth.signoutRedirect({
      id_token_hint: oidcAuth.user?.id_token,
      // Redirect to app origin; Azure B2C requires PKCE for the authorize URL, so we cannot
      // use the Azure login URL here. User can click Login to sign in again (with PKCE).
      post_logout_redirect_uri: window.location.origin,
    });
  };

  const value: AuthContextType = {
    user: convertOidcUserToUser(oidcAuth.user),
    currentUserId: backendUser?.id ?? null,
    hasVoucherPortalAccess: accessData?.has_voucher_portal_access ?? false,
    runningContractUntil: accessData?.running_contract_until ?? null,
    token: oidcAuth.user?.access_token || null,
    isLoading: oidcAuth.isLoading,
    isAccessLoading: oidcAuth.isAuthenticated ? isAccessLoading : false,
    isAuthenticated: oidcAuth.isAuthenticated,
    isAdmin: isAdminFromBackend,
    login,
    logout,
    error: oidcAuth.error || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
